import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Gauge, Calendar, Package, Clock, BarChart3, AlertTriangle } from "lucide-react";
import { getMinhaProdutividade } from "../services/relatorios";

function primeiroDiaMes() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function hojeISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function fmtDuracao(ms: number) {
  const totalMin = Math.round(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}min`;
  return `${h}h${String(m).padStart(2, "0")}`;
}

export function MinhaProdutividade() {
  const [inicio, setInicio] = useState(primeiroDiaMes());
  const [fim, setFim] = useState(hojeISO());

  const periodoValido = !!inicio && !!fim && inicio <= fim;
  const { data, isLoading, isError } = useQuery({
    queryKey: ["minha-produtividade", inicio, fim],
    queryFn: () => getMinhaProdutividade(inicio, fim),
    enabled: periodoValido,
    placeholderData: keepPreviousData,
  });

  const maxCat = Math.max(1, ...(data?.porCategoria.map((c) => c.quantidade) ?? [1]));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <Gauge size={22} className="text-blue-600" /> Minha produtividade
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">
            Suas entregas e tempo útil de trabalho no período.
          </p>
        </div>
        <div className="flex items-center gap-2 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-2">
          <Calendar size={16} className="text-slate-400" />
          <input type="date" value={inicio} max={fim} onChange={(e) => setInicio(e.target.value)} className="text-sm px-2 py-1 outline-none bg-transparent dark:text-slate-100" />
          <span className="text-slate-400">a</span>
          <input type="date" value={fim} min={inicio} onChange={(e) => setFim(e.target.value)} className="text-sm px-2 py-1 outline-none bg-transparent dark:text-slate-100" />
        </div>
      </div>

      {!periodoValido && (
        <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <AlertTriangle size={15} /> A data inicial precisa ser anterior ou igual à final.
        </div>
      )}
      {isError && periodoValido && (
        <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <AlertTriangle size={15} /> Erro ao carregar. Tente novamente.
        </div>
      )}

      {isLoading ? (
        <p className="text-slate-400 text-sm">Carregando…</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
              <p className="text-slate-500 dark:text-slate-400 text-sm flex items-center gap-1.5"><Package size={14} /> Entregas</p>
              <p className="text-3xl font-bold text-slate-800 dark:text-slate-100 mt-1">{data?.entregas ?? 0}</p>
            </div>
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
              <p className="text-slate-500 dark:text-slate-400 text-sm flex items-center gap-1.5"><Clock size={14} /> Tempo útil</p>
              <p className="text-3xl font-bold text-slate-800 dark:text-slate-100 mt-1">{fmtDuracao(data?.tempoUtilMs ?? 0)}</p>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2 text-slate-800 dark:text-slate-100">
              <BarChart3 size={18} /> Entregas por categoria
            </h3>
            {!data || data.porCategoria.length === 0 ? (
              <p className="text-slate-400 text-sm">Nenhuma entrega no período.</p>
            ) : (
              <div className="space-y-3">
                {data.porCategoria.map((c) => (
                  <div key={c.label} className="flex items-center gap-4">
                    <div className="w-40 text-sm truncate text-slate-700 dark:text-slate-200" title={c.label}>{c.label}</div>
                    <div className="flex-1 bg-slate-100 dark:bg-slate-800 rounded-full h-4 overflow-hidden">
                      <div className="bg-blue-500 h-full rounded-full transition-all" style={{ width: `${(c.quantidade / maxCat) * 100}%` }} />
                    </div>
                    <div className="w-12 text-right text-sm font-medium tabular-nums text-slate-700 dark:text-slate-200">{c.quantidade}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
